//
// Updatable Content
//
// Markup
//
//     <div class="js-socket-channel js-updatable-content"
//          data-channel="pull:123"
//          data-url="/pull/123?partial=commits">
//     </div>
//

import {fromEvent} from '@github-ui/subscription'
// eslint-disable-next-line no-restricted-imports
import {observe} from '@github/selector-observer'
import {updateContent} from '../updatable-content'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'

const MINIMUM_INTERVAL = 1000

class BatchedUpdatableContent {
  intervals: {
    [key: number]: {
      interval: NodeJS.Timer | null
      targets: Set<HTMLElement>
    }
  } = {}

  // Append each websocket update to the batch.
  onEvent(target: HTMLElement, windowSize: number) {
    let config = this.intervals[windowSize]

    if (!config) {
      config = this.intervals[windowSize] = {
        interval: null,
        targets: new Set(),
      }
    }

    config.targets.add(target)

    // Early return if we've already started.
    if (config.interval) return

    const fn = () => this.onInterval(windowSize)

    // Start the interval loop if it hasn't been started yet.
    config.interval = setInterval(fn, windowSize)
  }

  // Function run on the interval run.
  onInterval = (windowSize: number) => {
    // Move the reference.
    const config = this.intervals[windowSize]

    if (!config) return

    const targets = config.targets

    if (targets.size === 0) return

    // Clear the set so new messages can be batched up for the next iteration.
    config.targets = new Set()

    // Dispatch all queued updates.
    for (const target of targets) {
      // Only execute if we're attached to the DOM.
      if (document.body.contains(target)) {
        updateContent(target, {IS_BATCHED: 'true'})
      }
    }

    // Clear references.
    targets.clear()
  }

  // Clear the buffer so that intervals are not run after turbolinks navigations.
  clear = () => {
    for (const key in this.intervals) {
      if (Object.prototype.hasOwnProperty.call(this.intervals, key)) {
        const config = this.intervals[key]
        if (config) {
          // Stop the interval.
          if (config.interval) {
            clearInterval(config.interval)
            config.interval = null
          }

          config.targets.clear()
        }
      }
    }
  }
}

const batcher = new BatchedUpdatableContent()

window.addEventListener(SOFT_NAV_STATE.END, batcher.clear)

observe('.js-socket-channel.js-updatable-content', {
  subscribe: el =>
    fromEvent(el, 'socket:message', function (event: Event) {
      const {gid, wait} = (event as CustomEvent).detail.data
      const container = event.target as HTMLElement
      const target = gid ? findByGid(container, gid) : container

      /**
       * TODO: Ideally we'd have a better unique identifier to use to enable better scheduling. We'd have to introduce
       * a DOM attribute to give a unique identifier.
       */
      if (target) {
        const batched = target.getAttribute('data-batched')

        if (batched) {
          const windowSize = Math.max(parseInt(batched) || 0, MINIMUM_INTERVAL)
          batcher.onEvent(target, windowSize)
        } else {
          setTimeout(updateContent, wait || 0, target)
        }
      }
    }),
})

function findByGid(root: HTMLElement, gid: string): HTMLElement | null {
  if (root.getAttribute('data-gid') === gid) return root
  for (const el of root.querySelectorAll<HTMLElement>(`[data-url][data-gid]`)) {
    if (el.getAttribute('data-gid') === gid) {
      return el
    }
  }
  return null
}
